/**
 * Error Toast - Toast notifications for errors using react-toastify
 */

import { useTranslation } from 'react-i18next';
import { toast, ToastContainer, ToastOptions } from 'react-toastify';
import type { AppError } from '../../../utils/errors/types';
import { getErrorIcon } from '../../../utils/errors/types';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import LockIcon from '@mui/icons-material/Lock';
import i18n from '../../../i18n';

const defaultOptions: ToastOptions = {
  position: 'bottom-right',
  autoClose: 5000,
  hideProgressBar: false,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true,
  theme: 'colored',
};

export function showError(error: AppError | string): void {
  const err = typeof error === 'string' ? null : error;
  const message = typeof error === 'string' ? error : error.userMessage;

  toast.error(message, {
    ...defaultOptions,
    icon: getToastIcon(err),
  });
}

export function showSuccess(message: string): void {
  toast.success(message, defaultOptions);
}

export function showWarning(message: string): void {
  toast.warn(message, defaultOptions);
}

export function showInfo(message: string): void {
  toast.info(message, defaultOptions);
}

function getToastType(category: string): 'error' | 'warning' | 'info' {
  switch (category) {
    case 'network':
      return 'warning';
    case 'authentication':
      return 'error';
    case 'validation':
      return 'warning';
    default:
      return 'error';
  }
}

function getToastIcon(error: AppError | null) {
  if (!error) return undefined;
  
  const iconName = getErrorIcon(error.type);
  switch (iconName) {
    case 'wifi_off':
      return <WifiOffIcon />;
    case 'lock':
    case 'lock_open':
      return <LockIcon />;
    default:
      return <ErrorOutlineIcon />;
  }
}

export function ErrorToastContainer() {
  const { t } = useTranslation();

  return (
    <ToastContainer
      position="bottom-right"
      autoClose={5000}
      hideProgressBar={false}
      newestOnTop
      closeOnClick
      rtl={false}
      pauseOnFocusLoss
      draggable
      pauseOnHover
      theme="colored"
    />
  );
}

// Promise error handler for React Query / axios
export function handleQueryError(error: unknown): void {
  if (error instanceof Error || (error && typeof error === 'object' && 'message' in error)) {
    const message = (error as Error).message || i18n.t('errorToastDefaultMessage');
    showError(message);
  } else {
    showError(i18n.t('errorToastUnknownMessage'));
  }
}

export default {
  showError,
  showSuccess,
  showWarning,
  showInfo,
  ErrorToastContainer,
  handleQueryError,
};
